import React from 'react';
import styled from '@emotion/styled';
import { isString } from 'lodash';
import { TestId } from '@components/test-ids';
import { FiltersBarHeading, FilterSubHeading } from '@styles/filters';
import { useI18NextContext } from '@components/hooks';
import { FormControlLabel, Switch } from '@mui/material';
import { hex2rgba } from '@util/common';
import { Filter, FilterOption } from '@model/iceberg';
import { ThemeProps } from '@theme/base';

export interface ToggleFilterProps {
  testId?: string;
  filter: Filter;
  onChange: (filter: Filter) => any;
}

const Container = styled.div(({ theme }: ThemeProps) => ({
  padding: theme.custom.spacing.medium,
  ['.MuiSwitch-root']: {
    overflow: 'visible'
  },

  ['.MuiSwitch-thumb']: {
    boxShadow: 'none'
  },

  ['.MuiSwitch-switchBase']: {
    color: theme.custom.colors.group4.light,
    ['&.Mui-checked']: {
      color: theme.custom.colors.group1.base,

      ['& + .MuiSwitch-track']: {
        backgroundColor: theme.custom.colors.group2.light
      }
    }
  },

  ['.MuiSwitch-track']: {
    opacity: 1,
    backgroundColor: theme.custom.colors.group10.base
  },

  ['.MuiIconButton-root:hover']: {
    backgroundColor: hex2rgba(theme.custom.colors.group10.base, 0.2)
  },

  ['.MuiFormControlLabel-label']: {
    ...(theme.custom.typography.paragraph as any)
  }
}));

export const ToggleFilter = ({ filter, testId, onChange }: ToggleFilterProps) => {
  const t: any = useI18NextContext();

  const valueIsTrue = (value: string | boolean) => (isString(value) ? value === 'true' : value);

  const handleChange = () => {
    if (checked) {
      onChange({
        ...filter,
        values: filter.values.map((f: FilterOption) => ({ ...f, selected: !valueIsTrue(f.value) }))
      });
    } else {
      onChange({
        ...filter,
        values: filter.values.map((f: FilterOption) => ({ ...f, selected: valueIsTrue(f.value) }))
      });
    }
  };
  const label: string = t(`filters__toggle--label-${filter.name}`);
  const subTitleKey = `filters__toggle--subtitle-${filter.name}`;
  const subtitle: string = t(subTitleKey);
  const checked: boolean = !!filter.values.filter((f: FilterOption) => valueIsTrue(f.value) && f.selected).length;

  return (
    <Container data-testid={testId || TestId.filters.toggleFilter}>
      <FiltersBarHeading>{filter.formattedName}</FiltersBarHeading>
      {subtitle !== subTitleKey && <FilterSubHeading>{subtitle}</FilterSubHeading>}
      <FormControlLabel control={<Switch checked={checked} onChange={handleChange} value={checked} />} label={label} />
    </Container>
  );
};
